import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOutletContext, useParams } from 'react-router';
import type { MembershipSummary } from '../../../src/shared/auth';
import type {
  Appointment,
  AppointmentStatus,
  AppointmentCreateBody,
} from '../../../src/shared/scheduling';

import {
  schedulingKeys,
  fetchAppointments,
  fetchAppointment,
  createAppointment,
  updateAppointmentStatus,
  cancelAppointment,
  fetchAvailability,
} from '../../api/scheduling';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingScreen } from '../../components/LoadingScreen';
import { useRealtime } from '../../realtime/useRealtime';

const STATUS_FILTERS: { label: string; value: AppointmentStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Booked', value: 'booked' },
  { label: 'Completed', value: 'completed' },
  { label: 'Cancelled', value: 'cancelled' },
  { label: 'No Show', value: 'no_show' },
];

const STATUS_BADGES: Record<AppointmentStatus, string> = {
  booked: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-slate-100 text-slate-600',
  no_show: 'bg-red-100 text-red-700',
};

const TODAY_START = Math.floor(
  new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).getTime() / 1000
);
const WEEK_END = TODAY_START + 7 * 24 * 60 * 60;

function formatTimestamp(epoch: number): string {
  return new Date(epoch * 1000).toLocaleString();
}

function formatTime(epoch: number): string {
  return new Date(epoch * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function AppointmentListItem({
  appointment,
  isActive,
  onClick,
}: {
  appointment: Appointment;
  isActive: boolean;
  onClick: () => void;
}): React.JSX.Element {
  const startDate = new Date(appointment.startsAt * 1000);
  const dateStr = startDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
  const timeStr = formatTime(appointment.startsAt);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-lg border px-4 py-3 text-left text-sm transition-colors ${
        isActive
          ? 'border-slate-900 bg-slate-50'
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
      data-testid={`appointment-item-${appointment.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-medium text-slate-900">{appointment.contactId}</span>
        <span
          className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_BADGES[appointment.status] ?? 'bg-slate-100 text-slate-600'}`}
          data-testid={`appointment-status-${appointment.id}`}
        >
          {appointment.status.replace('_', ' ')}
        </span>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        {dateStr} &middot; {timeStr}
      </p>
      {appointment.notes !== '' && (
        <p className="mt-1 truncate text-xs text-slate-400">{appointment.notes}</p>
      )}
    </button>
  );
}

function AppointmentDetailView({
  appointment,
  onStatusChange,
  onCancel,
  isStatusPending,
  isCancelPending,
}: {
  appointment: Appointment;
  onStatusChange: (status: AppointmentStatus) => void;
  onCancel: () => void;
  isStatusPending: boolean;
  isCancelPending: boolean;
}): React.JSX.Element {
  const canCancel = appointment.status === 'booked';
  const statusTransitions: AppointmentStatus[] =
    appointment.status === 'booked' ? ['completed', 'no_show'] : [];

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
      data-testid="appointment-detail"
    >
      <h2 className="text-lg font-semibold text-slate-900">{appointment.contactId}</h2>

      <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="text-slate-500">Status</dt>
        <dd className="capitalize">{appointment.status.replace('_', ' ')}</dd>
        <dt className="text-slate-500">Date</dt>
        <dd>{formatTimestamp(appointment.startsAt)}</dd>
        <dt className="text-slate-500">Duration</dt>
        <dd>{Math.round((appointment.endsAt - appointment.startsAt) / 60)} minutes</dd>
        <dt className="text-slate-500">Lead</dt>
        <dd>{appointment.leadId ?? '—'}</dd>
        <dt className="text-slate-500">Assigned</dt>
        <dd>{appointment.assignedUserId ?? '—'}</dd>
        {appointment.notes !== '' && (
          <>
            <dt className="text-slate-500">Notes</dt>
            <dd>{appointment.notes}</dd>
          </>
        )}
        <dt className="text-slate-500">Created</dt>
        <dd>{formatTimestamp(appointment.createdAt)}</dd>
      </dl>

      {statusTransitions.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-medium text-slate-700">Update status</h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {statusTransitions.map((target) => (
              <button
                key={target}
                type="button"
                onClick={() => onStatusChange(target)}
                disabled={isStatusPending}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid={`status-btn-${target}`}
              >
                {target === 'no_show'
                  ? 'No Show'
                  : target.charAt(0).toUpperCase() + target.slice(1)}
              </button>
            ))}
          </div>
        </div>
      )}

      {canCancel && (
        <div className="mt-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={isCancelPending}
            className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="cancel-btn"
          >
            {isCancelPending ? 'Cancelling…' : 'Cancel appointment'}
          </button>
        </div>
      )}
    </div>
  );
}

function CreateAppointmentForm({
  slug,
  onCreated,
  onClose,
}: {
  slug: string;
  onCreated: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [selectedSlot, setSelectedSlot] = useState<{ startsAt: number; endsAt: number } | null>(
    null
  );
  const [contactId, setContactId] = useState('');
  const [leadId, setLeadId] = useState('');
  const [notes, setNotes] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  const availabilityQuery = useQuery({
    queryKey: schedulingKeys.availability.slots(slug, TODAY_START, WEEK_END),
    queryFn: ({ signal }) =>
      fetchAvailability(slug, { startsAt: TODAY_START, endsAt: WEEK_END }, signal),
  });

  const createMutation = useMutation({
    mutationFn: (body: AppointmentCreateBody) => createAppointment(slug, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: schedulingKeys.appointments.all(slug) });
      onCreated();
    },
  });

  const handleSubmit = () => {
    if (selectedSlot === null) {
      setFieldError('Select a time slot.');
      return;
    }
    if (contactId.trim().length === 0) {
      setFieldError('Contact id is required.');
      return;
    }
    setFieldError(null);
    const body: AppointmentCreateBody = {
      contactId: contactId.trim(),
      startsAt: selectedSlot.startsAt,
      endsAt: selectedSlot.endsAt,
      ...(leadId.trim().length > 0 ? { leadId: leadId.trim() } : {}),
      ...(notes.trim().length > 0 ? { notes: notes.trim() } : {}),
    };
    createMutation.mutate(body);
  };

  const slots = availabilityQuery.data?.slots ?? [];

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
      data-testid="create-appointment-form"
    >
      <h2 className="text-lg font-semibold text-slate-900">New appointment</h2>

      {availabilityQuery.isPending && <LoadingScreen />}
      {availabilityQuery.isError && <ErrorNotice error={availabilityQuery.error} />}

      {availabilityQuery.isSuccess && (
        <>
          <div className="mt-4">
            <h3 className="text-sm font-medium text-slate-700">Available slots</h3>
            {slots.length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">No available slots in the next 7 days.</p>
            ) : (
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {slots.map((slot, idx) => {
                  const startDate = new Date(slot.startsAt * 1000);
                  const dateStr = startDate.toLocaleDateString([], {
                    month: 'short',
                    day: 'numeric',
                  });
                  const timeStr = formatTime(slot.startsAt);
                  const isSelected = selectedSlot?.startsAt === slot.startsAt;
                  return (
                    <button
                      key={`${slot.startsAt}-${idx}`}
                      type="button"
                      onClick={() => setSelectedSlot(slot)}
                      className={`rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                        isSelected
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-300 text-slate-700 hover:border-slate-400'
                      }`}
                      data-testid={`slot-${slot.startsAt}`}
                    >
                      <span className="block text-xs text-inherit opacity-70">{dateStr}</span>
                      <span className="block font-medium">{timeStr}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-4 space-y-3">
            <div>
              <label htmlFor="appt-contact-id" className="block text-sm font-medium text-slate-700">
                Contact id
              </label>
              <input
                id="appt-contact-id"
                type="text"
                value={contactId}
                onChange={(e) => setContactId(e.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="appt-lead-id" className="block text-sm font-medium text-slate-700">
                Lead id (optional)
              </label>
              <input
                id="appt-lead-id"
                type="text"
                value={leadId}
                onChange={(e) => setLeadId(e.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="appt-notes" className="block text-sm font-medium text-slate-700">
                Notes (optional)
              </label>
              <textarea
                id="appt-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
              />
            </div>
          </div>

          {fieldError !== null && <p className="mt-3 text-sm text-red-600">{fieldError}</p>}
          {createMutation.isError && (
            <div className="mt-3">
              <ErrorNotice error={createMutation.error} />
            </div>
          )}

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={createMutation.isPending}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="create-appt-btn"
            >
              {createMutation.isPending ? 'Creating…' : 'Create appointment'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function AppointmentsPage(): React.JSX.Element {
  const { slug } = useParams<'slug'>();
  const { membership } = useOutletContext<{ membership: MembershipSummary }>();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<AppointmentStatus | 'all'>('all');
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const appointmentsQuery = useQuery({
    queryKey: schedulingKeys.appointments.list(
      slug!,
      statusFilter === 'all' ? undefined : statusFilter
    ),
    queryFn: ({ signal }) =>
      fetchAppointments(slug!, statusFilter === 'all' ? {} : { status: statusFilter }, signal),
    enabled: slug !== undefined,
  });

  const appointmentDetailQuery = useQuery({
    queryKey: schedulingKeys.appointments.detail(slug!, selectedAppointmentId!),
    queryFn: ({ signal }) => fetchAppointment(slug!, selectedAppointmentId!, signal),
    enabled: slug !== undefined && selectedAppointmentId !== null,
  });

  useRealtime(slug!);

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: AppointmentStatus }) =>
      updateAppointmentStatus(slug!, id, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: schedulingKeys.appointments.all(slug!) });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelAppointment(slug!, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: schedulingKeys.appointments.all(slug!) });
      setSelectedAppointmentId(null);
    },
  });

  if (slug === undefined) {
    return <p className="text-sm text-slate-500">Workspace slug not found.</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Appointments</h1>
          <p className="mt-1 text-sm text-slate-500">{membership.name}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setShowCreateForm(true);
            setSelectedAppointmentId(null);
          }}
          className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          data-testid="show-create-form-btn"
        >
          New appointment
        </button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Status filter">
        {STATUS_FILTERS.map((opt) => (
          <button
            key={opt.label}
            type="button"
            onClick={() => {
              setStatusFilter(opt.value);
              setSelectedAppointmentId(null);
            }}
            className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
              statusFilter === opt.value
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-300 text-slate-700 hover:bg-slate-100'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {appointmentsQuery.isError && (
        <div className="mt-4">
          <ErrorNotice error={appointmentsQuery.error} />
        </div>
      )}

      {cancelMutation.isError && (
        <div className="mt-4">
          <ErrorNotice error={cancelMutation.error} />
        </div>
      )}

      {statusMutation.isError && (
        <div className="mt-4">
          <ErrorNotice error={statusMutation.error} />
        </div>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-[360px_1fr]">
        <div className="space-y-2">
          {appointmentsQuery.isPending && <LoadingScreen />}

          {appointmentsQuery.isSuccess && appointmentsQuery.data.items.length === 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
              <p className="text-sm text-slate-500" data-testid="appointments-empty">
                {statusFilter !== 'all'
                  ? `No ${statusFilter.replace('_', ' ')} appointments found.`
                  : 'No appointments yet.'}
              </p>
            </div>
          )}

          {appointmentsQuery.isSuccess &&
            appointmentsQuery.data.items.map((appt) => (
              <AppointmentListItem
                key={appt.id}
                appointment={appt}
                isActive={selectedAppointmentId === appt.id}
                onClick={() => {
                  setSelectedAppointmentId(appt.id);
                  setShowCreateForm(false);
                }}
              />
            ))}
        </div>

        <div>
          {showCreateForm ? (
            <CreateAppointmentForm
              slug={slug}
              onCreated={() => {
                setShowCreateForm(false);
              }}
              onClose={() => setShowCreateForm(false)}
            />
          ) : appointmentDetailQuery.isPending && selectedAppointmentId !== null ? (
            <LoadingScreen />
          ) : appointmentDetailQuery.isError ? (
            <ErrorNotice error={appointmentDetailQuery.error} />
          ) : appointmentDetailQuery.data !== undefined ? (
            <AppointmentDetailView
              appointment={appointmentDetailQuery.data}
              onStatusChange={(status) =>
                statusMutation.mutate({ id: appointmentDetailQuery.data.id, status })
              }
              onCancel={() => cancelMutation.mutate(appointmentDetailQuery.data.id)}
              isStatusPending={statusMutation.isPending}
              isCancelPending={cancelMutation.isPending}
            />
          ) : (
            <div className="flex h-full min-h-[200px] items-center justify-center rounded-lg border border-slate-200 bg-white shadow-sm">
              <p className="text-sm text-slate-500">
                {showCreateForm ? '' : 'Select an appointment to view details.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
